import {
  AeSdk,
  AeSdkAepp,
  CompilerHttp,
  Encoded,
  Node,
} from "@aeternity/aepp-sdk";
import { storeToRefs } from "pinia";
import { ref } from "vue";

import { useAccounts, useNetworkStore, useTxQueueStore } from "@/stores";
import { watchUntilTruthy } from "@/utils/common";
import { NETWORK_MAINNET, NETWORK_TESTNET } from "@/utils/constants";
import { createDeepLinkUrl } from "@/utils/wallet";
import { useTransactionConfirmModal } from "@/stores/transactionConfirmModal";

let aeSdk: AeSdkAepp;
const nodes: { instance: Node; name: string }[] = [
  {
    name: NETWORK_MAINNET.name,
    instance: new Node(NETWORK_MAINNET.url),
  },
  {
    name: NETWORK_TESTNET.name,
    instance: new Node(NETWORK_TESTNET.url),
  },
];

const staticAeSdk = new AeSdk({
  onCompiler: new CompilerHttp("https://v7.compiler.aepps.com"),
  nodes,
});
const scanningForAccounts = ref(false);
const currentBlockHeight = ref(0);

export function useAeppSdk() {
  const { setAccounts, setActiveAccount } = useAccounts();
  const { activeAccount } = storeToRefs(useAccounts());
  const { transactionsQueue } = storeToRefs(useTxQueueStore());
  const { activeNetwork } = storeToRefs(useNetworkStore());
  const transactionConfirmModal = useTransactionConfirmModal();

  async function scanForAccounts() {
    scanningForAccounts.value = true;
    const currentAddress = Object.keys(
      aeSdk._accounts?.current || {}
    )[0] as any;

    setAccounts([currentAddress]);

    setActiveAccount(currentAddress);
    scanningForAccounts.value = false;
  }

  async function initSdk() {
    aeSdk = new AeSdkAepp({
      name: "AEAPP",
      nodes,
      onCompiler: new CompilerHttp(NETWORK_TESTNET.compilerUrl),
      onAddressChange: (a: any) => {
        setActiveAccount(Object.keys(a.current || {})[0] as any);
      },
      onDisconnect: () => {
        setActiveAccount(undefined);
        setAccounts([]);
      },
    });

    if (activeAccount.value) {
      addStaticAccount(activeAccount.value);
    }

    setInterval(async () => {
      getCurrentGeneration();
    }, 30000);
    getCurrentGeneration();
  }

  function getCurrentGeneration() {
    aeSdk.getCurrentGeneration().then((result) => {
      currentBlockHeight.value = result.keyBlock.height;
    });
  }

  function addStaticAccount(address: any) {
    setActiveAccount(address);
    staticAeSdk.addAccount(
      {
        address,
        signTransaction: function (
          tx: Encoded.Transaction
        ): Promise<Encoded.Transaction> {
          const uniqueId = Math.random().toString(36).substring(7);
          const currentUrl = new URL(window.location.href);
          // reset url
          currentUrl.searchParams.delete("transaction");
          currentUrl.searchParams.delete("status");

          const currentDomain = currentUrl.origin;

          // append transaction parameter for success case
          // const successUrl = new URL(currentUrl.href);
          const successUrl = new URL(`${currentDomain}/tx-queue/${uniqueId}`);
          successUrl.searchParams.set("transaction", "{transaction}");
          successUrl.searchParams.set("status", "completed");

          // append transaction parameter for failed case
          const cancelUrl = new URL(`${currentDomain}/tx-queue/${uniqueId}`);
          cancelUrl.searchParams.set("status", "cancelled");

          const signUrl: any = createDeepLinkUrl({
            type: "sign-transaction",
            transaction: tx,
            networkId: activeNetwork.value.networkId,
            "replace-caller": "true",
            // decode these urls because they will be encoded again
            "x-success": decodeURI(successUrl.href),
            "x-cancel": decodeURI(cancelUrl.href),
          });

          transactionsQueue.value[uniqueId] = {
            status: "pending",
            tx,
            signUrl,
          };

          return new Promise((resolve, reject) => {
            let newWindow: Window | null = null;
            const windowFeatures =
              "name=Superhero Wallet,width=362,height=594,toolbar=false,location=false,menubar=false,popup";

            transactionConfirmModal.openModal().then((confirmed: boolean) => {
              if (confirmed) {
                /**
                 * By setting a name and width/height,
                 * the extension is forced to open in a new window
                 */
                newWindow = window.open(signUrl, "_blank", windowFeatures);
              } else {
                reject(new Error("Transaction cancelled"));
              }
            });

            const interval = setInterval(() => {
              if (Object.keys(transactionsQueue.value).includes(uniqueId)) {
                if (transactionsQueue.value[uniqueId]?.status === "cancelled") {
                  clearInterval(interval);
                  reject(new Error("Transaction cancelled"));
                  newWindow?.close();
                  // delete transaction from queue
                  delete transactionsQueue.value[uniqueId];
                }

                if (
                  transactionsQueue.value[uniqueId]?.status === "completed" &&
                  transactionsQueue.value[uniqueId]?.transaction
                ) {
                  clearInterval(interval);
                  resolve(transactionsQueue.value[uniqueId].transaction);
                  newWindow?.close();
                  // delete transaction from queue
                  delete transactionsQueue.value[uniqueId];
                }
              }
            }, 500);
          });
        },
      } as any,
      { select: true }
    );
  }

  /**
   * Get the SDK instance. For now the SDK state is asynchronous.
   */
  async function getSdk(): Promise<AeSdkAepp> {
    return watchUntilTruthy(() => aeSdk);
  }

  /**
   * Get the SDK instance. For now the SDK state is asynchronous.
   */
  async function getAeSdk(): Promise<AeSdkAepp | AeSdk> {
    if (aeSdk?._accounts?.current) {
      return aeSdk;
    }
    return staticAeSdk;
  }

  return {
    getSdk,
    getAeSdk,
    aeSdk,
    staticAeSdk,
    nodes,
    initSdk,

    scanningForAccounts,
    addStaticAccount,

    scanForAccounts,
    currentBlockHeight,
  };
}
